import { apiClient } from "./client";
import { PaginationMeta } from "../lib/bufferedPagination";
import {
  AudienceFilters,
  AudienceSegment,
  AudienceTargetType,
  BroadcastNotification,
  ComposeBroadcastPayload,
  NotificationChannel,
  NotificationStatus,
  SelectedCustomer,
} from "../../features/notification/types";

type AnyObject = Record<string, unknown>;

const asObject = (value: unknown): AnyObject | null =>
  typeof value === "object" && value !== null ? (value as AnyObject) : null;

const asString = (value: unknown) => {
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return "";
};

const asNumber = (value: unknown, fallback = 0) =>
  typeof value === "number" && !Number.isNaN(value) ? value : fallback;

const CHANNEL_TO_API: Record<NotificationChannel, string> = {
  email: "EMAIL",
  push: "PUSH",
  sms: "SMS",
};

const CHANNEL_FROM_API: Record<string, NotificationChannel> = {
  EMAIL: "email",
  PUSH: "push",
  SMS: "sms",
};

const TARGET_TYPE_TO_API: Record<AudienceTargetType, string> = {
  segment: "SEGMENT",
  customer: "CUSTOMER",
};

const TARGET_TYPE_FROM_API: Record<string, AudienceTargetType> = {
  SEGMENT: "segment",
  CUSTOMER: "customer",
};

const SEGMENT_TO_API: Record<AudienceSegment, string> = {
  all: "ALL",
  approved: "APPROVED",
  "pending-review": "PENDING_REVIEW",
  rejected: "REJECTED",
};

const SEGMENT_FROM_API: Record<string, AudienceSegment> = {
  ALL: "all",
  APPROVED: "approved",
  PENDING_REVIEW: "pending-review",
  REJECTED: "rejected",
};

const toApiAudience = (audience: AudienceFilters) => ({
  targetType: TARGET_TYPE_TO_API[audience.targetType],
  segment: SEGMENT_TO_API[audience.segment],
  country: audience.country,
  currency: audience.currency,
  customers:
    audience.targetType === "customer"
      ? audience.customers.map((customer) => ({
          id: customer.id,
          label: customer.label,
          email: customer.email,
        }))
      : undefined,
});

const normalizeCustomers = (raw: unknown): SelectedCustomer[] => {
  if (!Array.isArray(raw)) return [];

  return raw
    .map((item) => {
      const record = asObject(item) ?? {};
      const nestedId = asObject(record.id);
      const id =
        (nestedId && (asString(nestedId._id).trim() || asString(nestedId.id).trim())) ||
        asString(record.id).trim();

      return {
        id,
        label: asString(record.label).trim(),
        email: asString(record.email).trim() || undefined,
      };
    })
    .filter((customer) => customer.id);
};

const normalizeAudience = (raw: unknown): AudienceFilters => {
  const record = asObject(raw) ?? {};

  return {
    targetType: TARGET_TYPE_FROM_API[asString(record.targetType).toUpperCase()] ?? "segment",
    segment: SEGMENT_FROM_API[asString(record.segment).toUpperCase()] ?? "all",
    country: asString(record.country) || "all",
    currency: asString(record.currency) || "all",
    customers: normalizeCustomers(record.customers),
  };
};

const STATUS_FROM_API: Record<string, NotificationStatus> = {
  DRAFT: "draft",
  IN_PROGRESS: "in-progress",
  SENT: "sent",
  FAILED: "failed",
};

const normalizeStatus = (value: unknown): NotificationStatus =>
  STATUS_FROM_API[asString(value).toUpperCase()] ?? "sent";

const normalizeBroadcastNotification = (
  item: unknown,
  index: number,
): BroadcastNotification => {
  const record = asObject(item) ?? {};
  const sentByObj = asObject(record.sentBy);
  const sentByName =
    asString(record.sentByName).trim() ||
    [asString(sentByObj?.firstName), asString(sentByObj?.lastName)]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    "Admin";

  return {
    id: asString(record._id).trim() || asString(record.id).trim() || `broadcast-${index}`,
    subject: asString(record.subject),
    message: asString(record.message),
    channel: CHANNEL_FROM_API[asString(record.channel).toUpperCase()] ?? "email",
    audience: normalizeAudience(record.audience),
    recipientCount: asNumber(record.recipientCount),
    status: normalizeStatus(record.status),
    sentBy: sentByName,
    sentAt:
      asString(record.sentAt).trim() ||
      asString(record.createdAt).trim() ||
      new Date().toISOString(),
  };
};

const getMeta = (payload: unknown): PaginationMeta | undefined => {
  const root = asObject(payload);
  const meta = root ? asObject(root.meta) : null;
  if (!meta) return undefined;

  return {
    currentPage: typeof meta.currentPage === "number" ? meta.currentPage : undefined,
    totalPages: typeof meta.totalPages === "number" ? meta.totalPages : undefined,
    hasNext: typeof meta.hasNext === "boolean" ? meta.hasNext : undefined,
    hasPrevious: typeof meta.hasPrevious === "boolean" ? meta.hasPrevious : undefined,
    total: typeof meta.totalItems === "number" ? meta.totalItems : undefined,
    itemsPerPage: typeof meta.itemsPerPage === "number" ? meta.itemsPerPage : undefined,
  };
};

export type GetBroadcastNotificationsParams = {
  page?: number;
  limit?: number;
  q?: string;
};

export type BroadcastNotificationsResult = {
  items: BroadcastNotification[];
  meta?: PaginationMeta;
};

export const getBroadcastNotifications = async (
  params: GetBroadcastNotificationsParams,
): Promise<BroadcastNotificationsResult> => {
  const response = await apiClient.get("/v1/broadcast-notification", { params });
  const root = asObject(response.data);
  const list = Array.isArray(root?.data) ? (root!.data as unknown[]) : [];

  return {
    items: list.map((item, index) => normalizeBroadcastNotification(item, index)),
    meta: getMeta(response.data),
  };
};

const toApiPayload = (payload: ComposeBroadcastPayload) => ({
  subject: payload.subject,
  message: payload.message,
  channel: CHANNEL_TO_API[payload.channel],
  audience: toApiAudience(payload.audience),
});

export const sendBroadcastNotification = async (
  payload: ComposeBroadcastPayload,
): Promise<BroadcastNotification> => {
  const response = await apiClient.post("/v1/broadcast-notification", toApiPayload(payload));
  const root = asObject(response.data);

  return normalizeBroadcastNotification(root?.data ?? response.data, 0);
};

export const saveBroadcastDraft = async (
  payload: ComposeBroadcastPayload,
): Promise<BroadcastNotification> => {
  const response = await apiClient.post("/v1/broadcast-notification", {
    ...toApiPayload(payload),
    isDraft: true,
  });
  const root = asObject(response.data);

  return normalizeBroadcastNotification(root?.data ?? response.data, 0);
};

export const updateBroadcastDraft = async (
  id: string,
  payload: ComposeBroadcastPayload,
  send: boolean,
): Promise<BroadcastNotification> => {
  const response = await apiClient.patch(`/v1/broadcast-notification/${id}`, {
    ...toApiPayload(payload),
    send,
  });
  const root = asObject(response.data);

  return normalizeBroadcastNotification(root?.data ?? response.data, 0);
};

export const deleteBroadcastDraft = async (id: string): Promise<void> => {
  await apiClient.delete(`/v1/broadcast-notification/${id}`);
};

export type CustomerSearchResult = {
  id: string;
  customerName: string;
  customerEmail: string;
  businessName: string;
  country: string;
};

export type SearchCustomersParams = {
  q?: string;
  page?: number;
  limit?: number;
};

export type SearchCustomersResult = {
  items: CustomerSearchResult[];
  meta?: PaginationMeta;
};

export const searchCustomers = async (
  params: SearchCustomersParams,
): Promise<SearchCustomersResult> => {
  const response = await apiClient.get("/v1/broadcast-notification/customers/search", {
    params,
  });
  const root = asObject(response.data);
  const list = Array.isArray(root?.data) ? (root!.data as unknown[]) : [];

  return {
    items: list.map((item) => {
      const record = asObject(item) ?? {};
      return {
        id: asString(record.id).trim(),
        customerName: asString(record.customerName),
        customerEmail: asString(record.customerEmail),
        businessName: asString(record.businessName),
        country: asString(record.country),
      };
    }),
    meta: getMeta(response.data),
  };
};
